import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import BackLink from "@/components/BackLink.jsx";
import { removeCartItem, updateCartItem } from "@/api/customer.api.js";
import { useCart } from "@/cart/CartContext.jsx";
import { useAuth } from "@/auth/AuthContext.jsx";
import { CHECKOUT_ROUTE, MAIN_ROUTE, ROLES } from "@/utils/constants.js";
import Price from "@/components/Price.jsx";
import ProductRecommendations from "@/components/ProductRecommendations.jsx";
import { productImageUrl } from "@/utils/format.js";
import "@/pages/customer-profile/profile.css";
import "./cart.css";

export default function CartPage() {
    const [error, setError] = useState("");
    const [updatingProductId, setUpdatingProductId] = useState(null);
    const [quantityDrafts, setQuantityDrafts] = useState({});
    const { items, setItems, refreshCart } = useCart();
    const { isAuthenticated, role } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (!isAuthenticated || role !== ROLES.CUSTOMER) {
            return;
        }
        refreshCart();
    }, [isAuthenticated, role, refreshCart]);

    useEffect(() => {
        setQuantityDrafts((prev) => {
            const next = {};
            for (const item of items) {
                const key = String(item.productId);
                next[key] = prev[key] !== undefined ? prev[key] : String(item.quantity);
            }
            return next;
        });
    }, [items]);

    const cartTotal = items.reduce((sum, item) => sum + Number(item.lineTotal || 0), 0);

    const handleQuantityChange = async (productId, quantity) => {
        setUpdatingProductId(productId);
        setError("");
        try {
            const data = quantity < 1
                ? await removeCartItem(productId)
                : await updateCartItem(productId, quantity);
            setItems(data.data || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setUpdatingProductId(null);
        }
    };

    const draftKey = (productId) => String(productId);

    const setDraftQuantity = (productId, raw) => {
        setQuantityDrafts((prev) => ({ ...prev, [draftKey(productId)]: raw }));
    };

    const commitQuantity = (item) => {
        const key = draftKey(item.productId);
        const raw = quantityDrafts[key];
        const parsed = Number(raw);
        if (raw === "" || !Number.isFinite(parsed) || parsed < 1) {
            setDraftQuantity(item.productId, String(item.quantity));
            return;
        }
        let next = Math.floor(parsed);
        if (item.stockQuantity != null && next > item.stockQuantity) {
            next = item.stockQuantity;
        }
        setDraftQuantity(item.productId, String(next));
        if (next === item.quantity) {
            return;
        }
        handleQuantityChange(item.productId, next);
    };

    const handleRemove = async (productId) => {
        setUpdatingProductId(productId);
        setError("");
        try {
            const data = await removeCartItem(productId);
            setItems(data.data || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setUpdatingProductId(null);
        }
    };

    return (
        <main className="container cart-page">
            <BackLink to={MAIN_ROUTE} className="cart-back">Continuer mes achats</BackLink>
            <h1>Mon panier</h1>

                {items.length === 0 ? (
                    <p className="muted">Votre panier est vide.</p>
                ) : (
                    <>
                        <div className="cart-list">
                            {items.map((item) => (
                                <article key={item.productId} className="card cart-row">
                                    {productImageUrl(item.photoId) ? (
                                        <img src={productImageUrl(item.photoId)} alt="" className="cart-row-thumb" />
                                    ) : (
                                        <div className="cart-row-thumb cart-row-thumb--empty" aria-hidden="true" />
                                    )}
                                    <div className="cart-row-body">
                                        <h2>{item.title}</h2>
                                        <p className="muted">{item.category}</p>
                                        <p><Price value={item.unitPrice} /></p>
                                    </div>
                                    <div className="cart-row-actions">
                                        <div className="order-line-qty-row cart-line-qty-row">
                                            <label className="field field--qty">
                                                Quantité
                                                <input
                                                    type="number"
                                                    min="1"
                                                    max={item.stockQuantity}
                                                    inputMode="numeric"
                                                    value={quantityDrafts[draftKey(item.productId)] ?? String(item.quantity)}
                                                    disabled={updatingProductId === item.productId}
                                                    onChange={(e) => {
                                                        const raw = e.target.value;
                                                        if (raw === "") {
                                                            setDraftQuantity(item.productId, "");
                                                            setError("");
                                                            return;
                                                        }
                                                        if (/^\d+$/.test(raw)) {
                                                            setDraftQuantity(item.productId, raw);
                                                            setError("");
                                                        }
                                                    }}
                                                    onBlur={() => commitQuantity(item)}
                                                />
                                            </label>
                                            <p className="order-line-subtotal">
                                                Sous-total :{" "}
                                                <strong><Price unitPrice={item.unitPrice} quantity={item.quantity} /></strong>
                                            </p>
                                        </div>
                                        <button
                                            type="button"
                                            className="link-btn cart-remove-btn"
                                            disabled={updatingProductId === item.productId}
                                            onClick={() => handleRemove(item.productId)}
                                        >
                                            Retirer
                                        </button>
                                    </div>
                                </article>
                            ))}
                        </div>

                        <div className="card cart-summary">
                            <p className="cart-total">Total : <strong><Price value={cartTotal} /></strong></p>
                            <button
                                type="button"
                                className="btn btn-primary btn-inline"
                                onClick={() => navigate(CHECKOUT_ROUTE)}
                            >
                                Passer commande
                            </button>
                        </div>
                    </>
                )}

                {error && <p className="error">{error}</p>}

                <ProductRecommendations
                    excludeCart
                    resetKey={items.map((item) => item.productId).join(",")}
                />
        </main>
    );
}
